/**
 * companySwitchTarget.js — 작업대상을 바꿨을 때 어느 화면으로 갈지 (2026-09-14)
 *
 * 전에는 선택창만 바뀌고 **화면은 그대로 남았다.** 미팅링크 매입 관리를 보다가
 * 유기자차로 바꿔도 미팅링크 매입 관리가 그대로 떠 있어서, 어느 회사를 보고 있는지
 * 화면과 선택창이 서로 다른 말을 했다.
 *
 * 규칙 (사용자 확정 2026-09-14):
 *   ① 회사별 짝이 있는 기능 15개 → 새 회사의 같은 기능으로 간다.
 *   ② 짝이 없는 업무 화면(매입 관리·흐름·예약·견적 …) → 홈으로 보낸다.
 *      타사를 고르면 그 메뉴들은 사이드바에서 아예 사라지므로 남겨 둘 자리가 없다.
 *   ③ 회사와 무관한 시스템 화면(로그·DB·테넌트 관리·IP·상황판 관리 …) → 그대로 둔다.
 *
 * 판정에 쓰는 재료는 전부 기존 표다 — 하드코딩한 주소 목록을 새로 만들지 않는다.
 */
import { COMPANY_MENU_FEATURES } from './companyFeatures';

export const HOME_PATH = '/admin/home';

/** 회사와 무관해서 작업대상을 바꿔도 그대로 둘 화면들. */
export function buildStayPaths(menuGroups, globalGroupNames, adminOnlyMenus = []) {
  const stay = new Set([HOME_PATH, '/admin', '/admin/board']);
  for (const g of menuGroups || []) {
    if (!globalGroupNames?.has(g.group)) continue;
    for (const item of g.items || []) if (item?.to) stay.add(item.to);
  }
  for (const m of adminOnlyMenus) if (m?.to) stay.add(m.to);
  return stay;
}

/** `/admin/ext/{코드}/{조각}` 이면 그 조각을, 아니면 null. */
function extPathSegment(pathname) {
  const m = String(pathname || '').match(/^\/admin\/ext\/[^/]+\/(.+)$/);
  return m ? m[1] : null;
}

/** 지금 보고 있는 화면이 표의 어느 기능인가. 아니면 null. */
export function featureOfPath(pathname) {
  const seg = extPathSegment(pathname);
  if (seg) return COMPANY_MENU_FEATURES.find((f) => f.path === seg) || null;
  const clean = String(pathname || '').replace(/\/+$/, '');
  return COMPANY_MENU_FEATURES.find((f) => f.hqPath && f.hqPath === clean) || null;
}

/**
 * 작업대상을 바꿨을 때 갈 곳.
 * @param {object} p
 * @param {string} p.pathname      지금 주소
 * @param {string} p.nextCode      새로 고른 회사 코드
 * @param {boolean} p.nextIsHq     그 회사가 이 사이트의 주인 회사인가
 * @param {Set<string>} p.stayPaths 그대로 둘 화면들 (buildStayPaths)
 * @returns {string|null} 이동할 주소. null 이면 그대로 둔다.
 */
export function resolveCompanySwitchTarget({ pathname, nextCode, nextIsHq, stayPaths }) {
  const here = String(pathname || '');
  const code = String(nextCode || '').trim();
  if (!code) return null;

  const feature = featureOfPath(here);
  if (feature) {
    const to = nextIsHq ? feature.hqPath : `/admin/ext/${code}/${feature.path}`;
    // 본사에 그 기능이 없으면(단말기 ID 관리) 돌려보낼 곳이 없다 → 홈
    if (!to) return HOME_PATH;
    return to === here ? null : to;
  }

  // 표에 없는 화면 — 회사와 무관한 것은 그대로, 그 외 업무 화면은 홈으로
  if (stayPaths?.has(here.replace(/\/+$/, ''))) return null;
  return here === HOME_PATH ? null : HOME_PATH;
}

/**
 * 통합관리에서 회사를 골랐을 때 '들어오기 전에 보던 회사 화면'을 그 회사에 맞춘 주소(2026-10-29 사용자 결정 나).
 *   lastPath = 마지막으로 본 회사 화면(utils/lastScreen — 왼쪽 메뉴에 보였던 통합 아닌 화면). 없으면 null.
 *   짝이 있는 기능은 고른 회사의 같은 기능, 회사와 무관한 화면은 그대로, 본사 전용 업무 화면은 본사를 골랐을 때만 그대로.
 *   돌려줄 곳이 홈뿐이면 HOME_PATH — 부르는 쪽(exitIntegratedTarget)이 홈 열쇠가 없으면 상황판·첫 주소로 바꾼다.
 */
export function backToCompanyScreen({ lastPath, nextCode, nextIsHq, stayPaths }) {
  if (!lastPath) return null;
  const to = resolveCompanySwitchTarget({ pathname: lastPath, nextCode, nextIsHq, stayPaths });
  if (to === HOME_PATH && nextIsHq && !featureOfPath(lastPath) && !/^\/admin\/ext\//.test(lastPath)) return lastPath;
  return to || lastPath; // null = 그 화면을 그대로 써도 된다
}
